// ─────────────────────────────────────────────────────────────────────────────
// NeedsMediaCard, right-rail "punch list" for content seats (content_creator /
// social_media_manager). Ports the legacy dashboard-wiring.js renderNeedsMedia /
// fetchNeedsMedia / openNeedsMediaModal / persistNeedsMediaUpdate (~3002-3175).
//
// Reads the band-scoped schedule blob and lists PAST confirmed shows still
// missing media: date < today AND date >= today-90d AND confirmed AND not
// _mediaSkipped AND no mediaLink. Sorted date DESC, capped at 5. Clicking a row
// opens a modal (portaled to <body>, a transformed ancestor otherwise traps the
// fixed overlay) to attach a media link or dismiss the notification. Persist
// re-reads the schedule fresh, mutates the matching event by id, and PUTs the
// SAME top-level shape back.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import { createPortal } from 'react-dom';
import { apiGet, apiPut } from '../../lib';

/** Minimal shape of a schedule event we touch here. */
interface ScheduleEvent {
  readonly id?: string | number;
  readonly date?: string;
  readonly venue?: string;
  readonly name?: string;
  readonly confirmed?: boolean;
  readonly mediaLink?: string;
  readonly _mediaSkipped?: boolean;
}

interface DueShow {
  readonly id: string;
  readonly date: string;
  readonly venue: string;
}

const SCHEDULE_PATH = '/api/data/schedule';

/** Unwrap the api response → the event array + a flag for how it was shaped. */
function unwrapSchedule(res: unknown): {
  readonly arr: ScheduleEvent[];
  readonly wrapped: boolean;
} {
  const raw =
    res && typeof res === 'object' && 'data' in res && (res as { data?: unknown }).data !== undefined
      ? (res as { data: unknown }).data
      : res;
  if (Array.isArray(raw)) return { arr: raw as ScheduleEvent[], wrapped: false };
  if (raw && typeof raw === 'object' && Array.isArray((raw as { value?: unknown }).value)) {
    return { arr: [...((raw as { value: ScheduleEvent[] }).value)], wrapped: true };
  }
  return { arr: [], wrapped: false };
}

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

/** Filter → past confirmed shows missing media, newest first, capped at 5. */
function toDueShows(arr: ScheduleEvent[]): DueShow[] {
  const todayIso = new Date().toISOString().slice(0, 10);
  const staleIso = isoDaysAgo(90);
  return arr
    .filter((s) => {
      if (!s || !s.date) return false;
      if (s.date >= todayIso) return false;
      if (s.date < staleIso) return false;
      if (!s.confirmed) return false;
      if (s._mediaSkipped) return false;
      return !s.mediaLink;
    })
    .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')))
    .slice(0, 5)
    .map((s) => ({
      id: String(s.id ?? ''),
      date: String(s.date ?? ''),
      venue: String(s.venue || s.name || 'Show'),
    }));
}

function shortDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso + 'T12:00:00');
  return isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function longDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso + 'T12:00:00');
  return isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function NeedsMediaCard(): JSX.Element {
  const [shows, setShows] = useState<DueShow[]>([]);
  const [active, setActive] = useState<DueShow | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      const res = await apiGet<unknown>('auth', SCHEDULE_PATH);
      setShows(toDueShows(unwrapSchedule(res).arr));
    } catch {
      setShows([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Re-read the schedule fresh, mutate the matching event, PUT the same shape back.
  const persist = useCallback(
    async (showId: string, patch: Partial<ScheduleEvent>): Promise<void> => {
      if (!showId) return;
      try {
        const res = await apiGet<unknown>('auth', SCHEDULE_PATH);
        const { arr, wrapped } = unwrapSchedule(res);
        let found = false;
        const next = arr.map((ev) => {
          if (String(ev.id ?? '') === String(showId)) {
            found = true;
            return { ...ev, ...patch };
          }
          return ev;
        });
        if (!found) return;
        await apiPut('auth', SCHEDULE_PATH, wrapped ? { value: next } : next);
      } catch {
        /* soft fail, next load reconciles */
      } finally {
        void load();
      }
    },
    [load]
  );

  const closeModal = useCallback(() => setActive(null), []);

  const onSave = useCallback(
    (link: string): void => {
      if (!active) return;
      let v = link.trim();
      if (!v) return;
      if (!/^https?:\/\//i.test(v)) v = 'https://' + v;
      const id = active.id;
      closeModal();
      void persist(id, { mediaLink: v });
    },
    [active, closeModal, persist]
  );

  const onDismiss = useCallback((): void => {
    if (!active) return;
    const id = active.id;
    closeModal();
    void persist(id, { _mediaSkipped: true });
  }, [active, closeModal, persist]);

  return (
    <>
      <section className="card needs-media-card" id="needs-media-card">
        <header className="card__head">
          <h3 className="card__title">Needs media</h3>
          <span className="tag">{shows.length}</span>
        </header>
        <div className="card__body needs-media-card__body">
          <ul className="needs-media-list">
            {shows.length === 0 ? (
              <li>
                <p className="empty-note">No shows are missing media, nice.</p>
              </li>
            ) : (
              shows.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    className="needs-media-row"
                    onClick={() => setActive(s)}
                  >
                    <span className="needs-media-row__date">{shortDate(s.date)}</span>
                    <span className="needs-media-row__venue">{s.venue}</span>
                    <svg
                      className="needs-media-row__chev"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="9 6 15 12 9 18" />
                    </svg>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      </section>

      {active &&
        createPortal(
          <NeedsMediaModal
            show={active}
            onSave={onSave}
            onDismiss={onDismiss}
            onClose={closeModal}
          />,
          document.body
        )}
    </>
  );
}

interface NeedsMediaModalProps {
  readonly show: DueShow;
  readonly onSave: (link: string) => void;
  readonly onDismiss: () => void;
  readonly onClose: () => void;
}

function NeedsMediaModal({ show, onSave, onDismiss, onClose }: NeedsMediaModalProps): JSX.Element {
  const [value, setValue] = useState('');
  const [invalid, setInvalid] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = window.setTimeout(() => inputRef.current?.focus(), 80);
    return () => window.clearTimeout(t);
  }, []);

  function attemptSave(): void {
    if (!value.trim()) {
      setInvalid(true);
      inputRef.current?.focus();
      return;
    }
    onSave(value);
  }

  return (
    <div
      className="needs-media-modal"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="needs-media-modal__card" role="dialog" aria-modal="true">
        <header className="needs-media-modal__head">
          <span className="needs-media-modal__eyebrow">Add media</span>
          <button
            type="button"
            className="needs-media-modal__close"
            aria-label="Close"
            onClick={onClose}
          >
            &times;
          </button>
        </header>
        <h3 className="needs-media-modal__title">{show.venue}</h3>
        {show.date && <p className="needs-media-modal__date">{longDate(show.date)}</p>}
        <label className="needs-media-modal__label">Dropbox / Drive / WeTransfer link</label>
        <input
          ref={inputRef}
          type="url"
          className="needs-media-modal__input"
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          placeholder="https://…"
          value={value}
          style={invalid ? { borderColor: '#8B1A2B' } : undefined}
          onChange={(e) => {
            setValue(e.target.value);
            if (invalid) setInvalid(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              attemptSave();
            }
            if (e.key === 'Escape') onClose();
          }}
        />
        <button type="button" className="needs-media-modal__save" onClick={attemptSave}>
          Save link
        </button>
        <button type="button" className="needs-media-modal__dismiss" onClick={onDismiss}>
          Dismiss notification
        </button>
        <button type="button" className="needs-media-modal__cancel" onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  );
}
